import { useCallback, useState } from "react";

import { LineageStatus } from "src/graphql";

/**
 * How many consecutive rebuilding responses a poller accepts before it stops
 * asking. A rebuild that has not finished by then is stuck.
 */
export const MAX_REBUILD_POLLS = 6;

export type RebuildPollLimit = {
  /** True once the poller has spent MAX_REBUILD_POLLS attempts without a ready graph. */
  hasGivenUp: boolean;
  /**
   * Called for every response from the polled query.
   */
  observe: (status: LineageStatus | undefined) => void;
  /** Reset the counter to start counting again. */
  reset: () => void;
};

/**
 * Bounds how long a lineage poller waits on a rebuild. Backoff comes from
 * useGovernedRefetchInterval, which stretches the interval while the graph
 * stops changing. This adds the stop: a caller polls while the status is
 * rebuilding and hasGivenUp is false, and shows a failure once it is true.
 */
export function useRebuildPollLimit(): RebuildPollLimit {
  const [pollCount, setPollCount] = useState(0);

  // Only a ready graph ends the wait, and it gives the next rebuild the full
  // count rather than what this one left behind. Every other outcome spends an
  // attempt, including a request that failed and therefore carries no status.
  const observe = useCallback((status: LineageStatus | undefined) => {
    setPollCount((current) =>
      status === LineageStatus.Ready ? 0 : current + 1,
    );
  }, []);

  const reset = useCallback(() => setPollCount(0), []);

  return { hasGivenUp: pollCount >= MAX_REBUILD_POLLS, observe, reset };
}
